import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Bus, Clock, MapPin, RefreshCw, Loader2, ShieldAlert, TrendingUp } from "lucide-react";
import { PageShell, PageHeader, StatCard, StatGrid, SectionCard, EmptyState, ActionButton, StatusBadge } from "@/components/ds";

const STATE_TONE = {
  en_circulation: "success",
  reserve: "info",
  pause: "yellow",
  fin_service: "neutral",
  depot: "muted",
  hors_service: "danger",
};

const STATE_LABEL = {
  en_circulation: "En circulation",
  reserve: "Réserve",
  pause: "Pause",
  fin_service: "Fin de service",
  depot: "Dépôt",
  hors_service: "Hors service",
};

export default function LineDetail() {
  const { routeId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState("");

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setTenantId(data.tenant_id)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/logibus/lines/${routeId}/overview`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [routeId]);

  if (!data) return (
    <PageShell>
      <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="animate-spin" size={16} /> Chargement de la ligne…</div>
    </PageShell>
  );

  const r = data.route;
  const onTimePct = data.on_time_pct;
  const onTimeTone = onTimePct == null ? "default" : onTimePct < 80 ? "danger" : "success";

  const copyPublicLink = () => {
    const url = `${window.location.origin}/public/route/${routeId}?tenant=${tenantId}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien public parents copié");
  };

  return (
    <PageShell testId="line-detail-page" dense>
      <Link to="/admin/live-tracking" data-testid="back-to-live" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-3">
        <ArrowLeft size={12} /> Retour Suivi live
      </Link>

      <PageHeader
        eyebrow="Vue détaillée ligne"
        title={
          <span className="flex items-center gap-4">
            <span
              className="w-14 h-14 inline-flex items-center justify-center bg-[#FFC60A] border-2 border-zinc-900 font-display font-black text-xl shadow-[3px_3px_0_0_#0A0A0B]"
              style={r.color ? { background: `#${r.color}`, color: r.color === "0A0A0B" ? "#FFC60A" : "#0A0A0B" } : undefined}
            >
              {r.route_short_name}
            </span>
            <span className="truncate">{r.route_long_name}</span>
          </span>
        }
        subtitle={`Suivi en temps réel · ${data.live_buses_count} bus en service`}
        actions={
          <>
            <ActionButton variant="ghost" icon={loading ? null : RefreshCw} loading={loading} onClick={load} testId="line-refresh">
              Recharger
            </ActionButton>
            <ActionButton variant="primary" icon={TrendingUp} onClick={copyPublicLink} disabled={!tenantId} testId="copy-public-link">
              Partager aux parents
            </ActionButton>
          </>
        }
      />

      {/* KPIs */}
      <StatGrid cols={5} className="mb-5">
        <StatCard testId="kpi-live-buses" icon={Bus} label="Bus en service" value={data.live_buses_count} tone={data.live_buses_count > 0 ? "success" : "default"} />
        <StatCard testId="kpi-trips" icon={Clock} label="Tournées GTFS" value={data.trips_total} />
        <StatCard testId="kpi-events" icon={ShieldAlert} label="Événements retard / jour" value={data.today_delay_events} tone={data.today_delay_events > 5 ? "warning" : "default"} />
        <StatCard testId="kpi-avg" icon={Clock} label="Retard moyen jour" value={data.today_avg_delay_min != null ? `${data.today_avg_delay_min} min` : "—"} />
        <StatCard testId="kpi-on-time" icon={TrendingUp} label="Ponctualité jour" value={onTimePct != null ? `${onTimePct}%` : "—"} tone={onTimeTone} />
      </StatGrid>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LIVE BUSES */}
        <SectionCard
          testId="live-buses-section"
          icon={Bus}
          title={`Bus en circulation (${data.live_buses_count})`}
          className="lg:col-span-2"
        >
          {data.live_buses.length === 0 ? (
            <EmptyState icon={Bus} title="Aucun bus actif" description="Aucun véhicule n'est en service sur cette ligne pour le moment." />
          ) : (
            <div className="divide-y divide-zinc-200">
              {data.live_buses.map((b) => (
                <div key={b.vehicle_id} className="p-3" data-testid={`live-bus-${b.vehicle_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                    <div className="font-display font-bold text-sm tracking-tight">{b.bus_name}</div>
                    <StatusBadge tone={STATE_TONE[b.service_status] || "muted"} label={STATE_LABEL[b.service_status] || b.service_status} size="sm" withDot={false} />
                  </div>
                  <div className="text-[11px] font-mono text-zinc-600 grid grid-cols-2 gap-x-3 gap-y-0.5">
                    {b.driver_name && <div className="truncate">👤 {b.driver_name}</div>}
                    {b.detected_trip_id && <div className="truncate">🚏 {b.detected_trip_id}</div>}
                    {b.next_stop_name && (
                      <div className="col-span-2 truncate">
                        <MapPin size={10} className="inline mr-0.5 -mt-0.5" /> {b.next_stop_name}
                        {b.next_stop_eta_seconds != null && ` · ETA ${Math.round(b.next_stop_eta_seconds / 60)} min`}
                      </div>
                    )}
                    {b.delay_minutes != null && b.delay_minutes !== 0 && (
                      <div className={`col-span-2 font-bold ${b.delay_minutes > 0 ? "text-orange-700" : "text-blue-700"}`}>
                        Retard {b.delay_minutes > 0 ? "+" : ""}{b.delay_minutes} min
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* SIDE */}
        <aside className="space-y-4">
          <SectionCard
            testId="next-departures"
            icon={Clock}
            title="Prochains départs"
          >
            {data.upcoming_departures.length === 0 ? (
              <EmptyState dense title="Aucun départ planifié" description="Plus de départ planifié à venir aujourd'hui." />
            ) : (
              <div className="divide-y divide-zinc-200">
                {data.upcoming_departures.map((u) => (
                  <div key={u.trip_id} className="px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-sm">{u.first_departure?.slice(0, 5) || "—"}</span>
                      <span className="text-[10px] font-mono text-zinc-400 truncate ml-2">{u.trip_id}</span>
                    </div>
                    {u.headsign && <div className="text-[11px] text-zinc-600 mt-0.5 truncate">→ {u.headsign}</div>}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            testId="open-incidents-list"
            tone="danger"
            icon={ShieldAlert}
            title={`Incidents ouverts (${data.open_incidents.length})`}
          >
            {data.open_incidents.length === 0 ? (
              <EmptyState dense title="Aucun incident" description="Aucun incident ouvert sur cette ligne." />
            ) : (
              <div className="divide-y divide-zinc-200">
                {data.open_incidents.map((i) => (
                  <div key={i.id} className="px-3 py-2 text-xs">
                    <div className="font-bold">{i.title}</div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                      {i.severity?.toUpperCase()} · {i.timestamp?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </aside>
      </div>
    </PageShell>
  );
}
